import type { LucideIcon } from 'lucide-react';
import { DeltaPill } from '@/components/DeltaPill';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { Section } from '@/components/Section';
import { Skeleton } from '@/components/Skeleton';
import { calculateDelta, formatNumber } from '@/utils';
import * as styles from './GlobalScore.css';

type GlobalScoreProps = {
    /**
     * The icon to display inside the circle on the left. This should be a Lucide icon component.
     */
    icon: LucideIcon;
    /**
     * The title to display above the value (e.g., 'DI 平均').
     */
    title: string;
    /**
     * The scores to display, or `null` while they are still loading.
     *
     * Grouped rather than passed as separate props because `value` is itself
     * nullable when a score can't be computed — a loaded state that renders as
     * 'N/A', which a null `value` alone couldn't be told apart from.
     */
    scores: {
        /**
         * The main value to display. If null, 'N/A' will be shown instead.
         */
        value: number | null;
        /**
         * The previous value to compare against. When provided, a delta pill and '前回: X.X' subtext are shown.
         */
        previousValue?: number | null;
    } | null;
    /** Opt-in: omit when this data has no independent failure mode to report. */
    isError?: boolean;
    onRetry?: () => void;
};

export function GlobalScore({ icon, title, scores, isError, onRetry }: GlobalScoreProps) {
    if (isError) return <ErrorOverlay active onRetry={onRetry} message={`${title}の取得に失敗しました。`} />;

    if (!scores) return <GlobalScoreSkeleton />;

    return <GlobalScoreContent icon={icon} title={title} {...scores} />;
}

type GlobalScoreContentProps = Omit<GlobalScoreProps, 'scores' | 'isError' | 'onRetry'> &
    NonNullable<GlobalScoreProps['scores']>;

// Split from the buffer above so its rendering stays free of loading concerns.
function GlobalScoreContent({ icon, title, value, previousValue }: GlobalScoreContentProps) {
    const delta = value != null && previousValue != null ? calculateDelta(value, previousValue) : null;

    return (
        <Section pt="8" pb="8" flex>
            <Flex gap="8" align="center">
                <div className={styles.iconCircle}>
                    <Icon icon={icon} size="lg" className={styles.icon} />
                </div>

                <Flex direction="column" gap="2">
                    <div className={styles.title}>{title}</div>

                    <Flex align="center" gap="3">
                        <div className={styles.value}>{value !== null ? formatNumber(value) : 'N/A'}</div>
                        {delta != null && <DeltaPill delta={delta} />}
                    </Flex>

                    {previousValue != null && <div className={styles.subtext}>前回: {formatNumber(previousValue)}</div>}
                </Flex>
            </Flex>
        </Section>
    );
}

export function GlobalScoreSkeleton() {
    return (
        <Section pt="8" pb="8" flex>
            <Flex gap="8" align="center">
                <Skeleton.Circle radius={56} />

                <Flex direction="column" gap="4" flex>
                    <Skeleton.Line width="30%" height="sm" />
                    <Skeleton.Line width="60%" height="4xl" />
                    <Skeleton.Line width="30%" height="sm" />
                </Flex>
            </Flex>
        </Section>
    );
}
